import { createColumnHelper } from '@tanstack/react-table';
import { HeadCell, SortableHeadCell, TextCell } from 'components/tables/cells';
import { TextColumnProps } from '../tables/columnTypes';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import moment, { Moment } from 'moment';
import { BlockActionsCell } from './AccountsList/BlockActionsCell';
import { Email } from 'components/common/Email/Email';
import { ProductAccessListCell } from './AccountsList/ProductAccessListCell/ProductAccessListCell';
import { sortByDate } from 'components/tables/sorting/sortByDate';
import { MontelAccount } from 'domain/account/interfaces';
import { Dispatch, SetStateAction } from 'react';
import { AccountStatus } from 'components/common/AccountStatus/AccountStatus';

const columnHelper = createColumnHelper<MontelAccount>();

export const accountTableColumns = {
	fullName: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
			id: 'fullName',
			enableSorting: true,
			size: 100,
			header: ({ header }) => (
				<SortableHeadCell
					text="Full name"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),

	email: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor((row) => row.email.value, {
			id: 'email',
			enableSorting: true,
			filterFn: (row, id, value) => {
				if (value === 'hide-unverified') {
					return row.original.email.verified === true;
				}
				return true;
			},
			header: ({ header }) => (
				<SortableHeadCell
					text="Email"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => <Email email={info.row.original.email} />,
			...overrideProps?.columnDefProps,
		}),

	phoneNumber: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor((row) => row.phoneNumber, {
			id: 'telephone',
			enableSorting: true,
			enableGlobalFilter: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Phone number"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue() || 'N/A'}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),

	jobTitle: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor((row) => row.company.jobTitle, {
			id: 'jobTitle',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Job title"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue() || 'N/A'}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),

	registeredCompany: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor((row) => row.company.name, {
			id: 'registeredCompany',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Company"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue() || 'N/A'}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),

	accountCreated: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor(
			(row) => toDefaultDateFormatOrNotAvailable(row.createdAt),
			{
				id: 'accountCreated',
				enableSorting: true,
				enableGlobalFilter: false,
				filterFn: (row, id, value: Moment) => {
					if (typeof value === 'undefined') return true;

					return moment(row.original.createdAt).isAfter(value, 'day');
				},
				sortingFn: (a, b) =>
					sortByDate(a.original.createdAt, b.original.createdAt),
				header: ({ header }) => (
					<SortableHeadCell
						text="Account created (UTC)"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => (
					<TextCell
						text={info.getValue()}
						{...overrideProps?.cellProps}
					/>
				),
				...overrideProps?.columnDefProps,
			},
		),

	appAccess: () =>
		columnHelper.accessor((row) => row.productRegistrations, {
			id: 'appAccess',
			enableSorting: false,
			header: () => <HeadCell text="App access" />,
			cell: (info) => (
				<ProductAccessListCell
					productRegistrations={
						info.row.original.productRegistrations
					}
				/>
			),
		}),

	activeStatus: (overrideProps?: TextColumnProps<MontelAccount>) =>
		columnHelper.accessor((row) => (row.locked ? 'Locked' : 'Active'), {
			id: 'activeStatus',
			enableSorting: true,
			enableGlobalFilter: false,
			filterFn: (row, id, value) => {
				switch (value) {
					case 'all':
						return true;
					case 'active':
						return row.original.locked === false;
					case 'locked':
						return row.original.locked === true;
					case 'reserved':
						return row.original.hasPendingInvitation === true;
					default:
						return true;
				}
			},
			header: ({ header }) => (
				<SortableHeadCell
					text="Account status"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<AccountStatus
					locked={info.row.original.locked}
					hasPendingInvitation={
						info.row.original.hasPendingInvitation
					}
				/>
			),
			...overrideProps?.columnDefProps,
		}),

	blockActions: (
		setSelectedAccount: (account: MontelAccount) => void,
		setShowBlockAccountModal: Dispatch<SetStateAction<boolean>>,
		setShowReopenAccountModal: Dispatch<SetStateAction<boolean>>,
	) =>
		columnHelper.accessor(() => null, {
			id: 'actions',
			enableSorting: false,
			meta: { width: '120px', disableRowLink: true },
			header: () => <HeadCell text="" />,
			cell: (info) => (
				<BlockActionsCell
					account={info.row.original}
					onSuccess={setSelectedAccount}
					setShowBlockAccountModal={setShowBlockAccountModal}
					setShowReopenAccountModal={setShowReopenAccountModal}
				/>
			),
		}),
};
